import type { Extend } from '~/types/type-helpers';
import {
    addArticleToGrid,
    evaluateGrid,
    getLayouts,
    getColumnCount,
    getEmptyGrid,
    getSizedEntities,
    splitIntoChunks,
    splitIntoFullBlocks,
    optimizeGrid,
    stackGrids,
    getChunkSize,
} from './helpers';
import type {
    SizedEntity,
    EvaluatedSolution,
    Grid,
    GetId,
    GetScore,
    Penalize,
    Boost,
} from './types';

const getSolutions = <T>(
    entities: SizedEntity<T>[],
    grid: Grid,
    getId: GetId<T>,
    penalize?: Penalize<T>,
    boost?: Boost<T>,
    solutions: Grid[] = []
) => {
    const currentEntity = entities[0];
    const layouts = getLayouts(
        currentEntity,
        getId,
        getColumnCount(grid),
        penalize,
        boost
    );

    let newSolutions = solutions;

    for (const layout of layouts) {
        try {
            const newGrid = addArticleToGrid(grid, layout);
            const remainingArticles = entities.slice(1);
            if (remainingArticles.length) {
                const childSolutions = getSolutions(
                    remainingArticles,
                    newGrid,
                    getId,
                    penalize,
                    boost,
                    solutions
                );
                newSolutions = [...newSolutions, ...childSolutions];
            } else {
                newSolutions = [...newSolutions, newGrid];
            }
        } catch {
            /** skip to next layout */
        }
    }

    return newSolutions;
};

export const exploreSolutions = <T>(
    entities: T[],
    gridSize: number,
    opts: {
        getId: GetId<T>;
        getScore: GetScore<T>;
        penalize?: Penalize<T>;
        boost?: Boost<T>;
    }
): EvaluatedSolution[] => {
    const { getId, getScore, penalize, boost } = opts;

    const sizedEntities = getSizedEntities(entities, gridSize, getScore);
    const initialGrid = getEmptyGrid(gridSize);
    const solutions = getSolutions(
        sizedEntities,
        initialGrid,
        getId,
        penalize,
        boost
    );
    const evaluatedSolutions = solutions.map<EvaluatedSolution>((solution) => ({
        grid: solution,
        badness: evaluateGrid(solution),
    }));
    return evaluatedSolutions;
};

const findSolution = <T>(
    entities: SizedEntity<T>[],
    grid: Grid,
    badnessThreshold: number,
    getId: GetId<T>,
    penalize?: Penalize<T>,
    boost?: Boost<T>
): Grid => {
    let bestSolution!: EvaluatedSolution;

    const _findSolution = (
        entities: SizedEntity<T>[],
        grid: Grid,
        badnessThreshold: number
    ): Grid | undefined => {
        const currentEntity = entities[0];
        const layouts = getLayouts(
            currentEntity,
            getId,
            getColumnCount(grid),
            penalize,
            boost
        );

        for (const layout of layouts) {
            try {
                const newGrid = addArticleToGrid(grid, layout);
                const remainingArticles = entities.slice(1);
                if (remainingArticles.length) {
                    const childSolution = _findSolution(
                        remainingArticles,
                        newGrid,
                        badnessThreshold
                    );
                    if (childSolution !== undefined) {
                        return childSolution;
                    }
                } else {
                    const badness = evaluateGrid(newGrid);
                    if (badness <= badnessThreshold) {
                        return newGrid;
                    }
                    if (!bestSolution || bestSolution.badness > badness) {
                        bestSolution = {
                            grid: newGrid,
                            badness,
                        };
                    }
                }
            } catch {
                /** skip to next layout */
            }
        }

        return undefined;
    };

    const solution = _findSolution(entities, grid, badnessThreshold);
    return solution || bestSolution.grid;
};

type GetResultOptions<T> = {
    badnessThreshold?: number;
    getId: GetId<T>;
    getScore: GetScore<T>;
    penalize?: Penalize<T>;
    boost?: Boost<T>;
    optimize?: boolean;
    split?: boolean;
};

export function getResult<T>(
    entities: T[],
    gridSize: number,
    opts: Extend<GetResultOptions<T>, { split: true }>
): Array<Grid> | undefined;

export function getResult<T>(
    entities: T[],
    gridSize: number,
    opts: Extend<GetResultOptions<T>, { split?: false }>
): Grid | undefined;

export function getResult<T>(
    entities: T[],
    gridSize: number,
    opts: GetResultOptions<T>
): Array<Grid> | Grid | undefined {
    const {
        badnessThreshold = 0,
        getId,
        getScore,
        penalize,
        boost,
        optimize,
        split,
    } = opts;

    if (!entities.length) {
        return undefined;
    }

    const sizedEntities = getSizedEntities(entities, gridSize, getScore);
    const chunkSize = getChunkSize(8, sizedEntities.length, gridSize);
    const chunks = splitIntoChunks(sizedEntities, chunkSize);

    const grids = chunks.map<Grid>((chunk) =>
        findSolution(
            chunk,
            getEmptyGrid(gridSize),
            badnessThreshold,
            getId,
            penalize,
            boost
        )
    );

    if (split) {
        const gridBlocks = grids.flatMap(splitIntoFullBlocks);
        return optimize ? gridBlocks.map(optimizeGrid) : gridBlocks;
    }
    return stackGrids(optimize ? grids.map(optimizeGrid) : grids);
}
